import { AJUDA_COMECAR } from "@/lib/ajuda/dialogos/comecar";
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { ROTULO_DA_FAMILIA_DE_MATERIAL, chaveDoProprio, type AbrangenciaDoServico } from "@bookdental/schemas";
import { CampoAbrangencia } from "@/components/catalogo/CampoCategoria";
import { MateriaisECoresDoServico, type OfertaDeMaterial } from "@/components/catalogo/MateriaisECoresDoServico";
import { porFamilia, type EscalaDaBase, type MaterialDaBase, type MaterialProprio, type ServicoEscolhido } from "./tipos";

/**
 * Um serviço de cada vez: se o dentista marca dente ou arcada, em que materiais ele é feito (a base
 * sugere) e as escalas de cor de cada material — o mesmo miolo do dialog do Catálogo, guardando no rascunho em vez do servidor.
 */
export function ConfigurarServicoDialog({
  servico,
  onFechar,
  padrao,
  atuais,
  materiais,
  proprios,
  onNovoProprio,
  escalas,
  escalasPorMaterial,
  abrangencia,
  onSalvar,
}: {
  servico: ServicoEscolhido | null;
  onFechar: () => void;
  /** Os compatíveis na base — ficam à vista; o resto em "Ver todos". */
  padrao: string[];
  atuais: string[];
  materiais: MaterialDaBase[];
  proprios: MaterialProprio[];
  onNovoProprio: (nome: string, familia?: string) => void;
  escalas: EscalaDaBase[];
  escalasPorMaterial: Record<string, string[]>;
  abrangencia: AbrangenciaDoServico;
  onSalvar: (materiais: string[], escalasPorMaterial: Record<string, string[]>, abrangencia: AbrangenciaDoServico) => void;
}) {
  const [marcados, setMarcados] = useState<string[]>([]);
  const [escalasAqui, setEscalasAqui] = useState<Record<string, string[]>>({});
  const [abrangenciaAqui, setAbrangenciaAqui] = useState<AbrangenciaDoServico>("DENTE");
  useEffect(() => {
    if (!servico) return;
    setMarcados(atuais);
    setEscalasAqui(escalasPorMaterial);
    setAbrangenciaAqui(abrangencia);
    // Só ao abrir: depois quem manda é o que a pessoa mexe aqui.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servico?.chave]);

  const aVista = new Set([...padrao, ...marcados]);
  const daBase = porFamilia(materiais);
  const oferta = (m: MaterialDaBase): OfertaDeMaterial => ({ id: m.id, label: m.nome, grupo: ROTULO_DA_FAMILIA_DE_MATERIAL[m.familia ?? ""] ?? "Outros" });
  const ofertas: OfertaDeMaterial[] = [
    ...daBase.filter((m) => aVista.has(m.id)).map(oferta),
    ...proprios.map((p) => ({ id: chaveDoProprio(p.nome), label: p.nome, grupo: p.familia ?? "Criados por você" })),
  ];
  const outras = daBase.filter((m) => !aVista.has(m.id)).map(oferta);

  return (
    <Dialog open={!!servico} onOpenChange={(v) => !v && onFechar()}>
      <DialogContent className="flex max-h-[88dvh] max-w-2xl flex-col gap-3 overflow-y-auto border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle ajuda={AJUDA_COMECAR.configurarServico}>{servico?.nome}</DialogTitle>
          <DialogDescription>{servico?.categoria} · o que o dentista poderá escolher neste trabalho.</DialogDescription>
        </DialogHeader>

        <CampoAbrangencia valor={abrangenciaAqui} onChange={setAbrangenciaAqui} />

        <MateriaisECoresDoServico
          ofertas={ofertas}
          outras={outras}
          materialIds={marcados}
          onMaterialIds={setMarcados}
          escalasPorMaterial={escalasAqui}
          onEscalasPorMaterial={setEscalasAqui}
          escalas={escalas.map((e) => ({ codigo: e.codigo, rotulo: e.nome }))}
          onNovoMaterial={(nome, familia) => {
            const chave = chaveDoProprio(nome);
            if (!proprios.some((p) => p.nome.toLowerCase() === nome.toLowerCase())) onNovoProprio(nome, familia);
            if (!marcados.includes(chave)) setMarcados([...marcados, chave]);
          }}
        />

        <div className="flex items-center justify-between gap-2 border-t border-zinc-100 pt-3 dark:border-border">
          <button
            type="button"
            onClick={() => setMarcados(padrao)}
            className="text-xs font-medium text-zinc-500 hover:text-zinc-800 hover:underline dark:text-muted-foreground"
          >
            Voltar ao sugerido
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onFechar}
              className="inline-flex h-9 items-center rounded-md border border-zinc-200 px-3 text-sm font-medium text-zinc-600 hover:bg-zinc-50 dark:border-border dark:text-muted-foreground"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => onSalvar(marcados, escalasAqui, abrangenciaAqui)}
              className="inline-flex h-9 items-center rounded-md bg-brand-600 px-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
            >
              Salvar
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
